import { format } from "date-fns"
import { useQuery } from "@tanstack/react-query"
import { CalendarClock, CreditCard } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/custom/button"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { Separator } from "@/modules/shared/ui/components/separator"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { getPendingSubscription } from "@/modules/subscriptions/infrastructure/subscription.api"
import { useInitiateSubscriptionPayment } from "@/modules/subscriptions/hooks/useInitiateSubscriptionPayment"
import { setPendingPaymentReturn } from "@/lib/payment-return"

export function ActivatePlanPage() {
  const { currentOrganization } = useOrganizationsService()
  const organizationId = currentOrganization?.id

  const {
    data: pendingSub,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["subscription", "pending", organizationId],
    queryFn: () => getPendingSubscription(organizationId!),
    enabled: !!organizationId,
    retry: false,
  })

  const { mutate: initiatePayment, isPending: isInitiating } =
    useInitiateSubscriptionPayment()

  const handlePayNow = () => {
    if (!pendingSub || !organizationId) return
    setPendingPaymentReturn({ organizationId, returnPath: "/dashboard" })
    initiatePayment(pendingSub.id)
  }

  const trialEndFormatted = pendingSub?.trialEndDate
    ? format(new Date(pendingSub.trialEndDate), "d MMMM yyyy")
    : null

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CalendarClock className="h-16 w-16 text-amber-500" />
          </div>
          <CardTitle className="text-2xl">Trial Period Ended</CardTitle>
          <CardDescription>
            Your free trial has expired. Activate your plan to continue using
            your organisation.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {isError && (
            <p className="text-sm text-muted-foreground text-center">
              No pending subscription found.{" "}
              <a href="/support" className="underline text-primary">
                Contact support
              </a>{" "}
              if you need help.
            </p>
          )}

          {pendingSub && (
            <>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-medium">{pendingSub.planName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Billing</span>
                  <span className="font-medium">
                    {pendingSub.billingInterval}
                  </span>
                </div>
                {trialEndFormatted && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trial ended</span>
                    <span className="font-medium text-destructive">
                      {trialEndFormatted}
                    </span>
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>R{pendingSub.price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VAT (15%)</span>
                  <span>R{pendingSub.vatAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-1">
                  <span>Total</span>
                  <span>R{pendingSub.totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={handlePayNow}
                disabled={isInitiating}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {isInitiating ? "Redirecting..." : "Pay Now"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
